import React from 'react'
import {BRAND_LABEL} from '../brand'
import {findImageGroupBy} from './image-groups-utils'

/**
 * Clears unwanted spaces of a string value separated by token and returns an array
 *
 * @param values - string (ex. 'category , question,answer')
 * @param token - string (ex. ',')
 * @return {array} - => ['category', 'question', 'answer']
 */
export const sanitizeValues = (values, token = ',') => values.replace(/\s/g, '').split(token)
export const remToPxConverter = (remValue) => parseFloat(remValue) * 16
export const sortAlphabetically = (a, b) => {
    if (a.label < b.label) return -1
    if (a.label > b.label) return 1
    return 0
}
/**
 * Formats a given date (or the current date) as "YYYY-MM-DD".
 *
 * @param {Date} [date=new Date()] - The date to format. Defaults to the current date.
 * @returns {string} The formatted date in "YYYY-MM-DD" format.
 */
export const formatDateToYYYYMMDD = (date = new Date()) => {
    return date.toISOString().split('T')[0] // Extracts only the date part
}
/**
 * Formats a given date string as "YYYYMMDD000000".
 *
 * @param {string} dateStr - The date string to format in "DD/MM/YYYY" format.
 * @returns {string} The formatted date in "YYYYMMDD000000" format.
 */
export const convertDateToYYYYMMDD000000 = (dateStr = '') => {
    if (!dateStr) {
        console.error('Invalid date string:', dateStr)
        return ''
    }

    const date = new Date(dateStr)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}000000`
}

/**
 * Converts a date string in "YYYYMMDD000000" format to a JavaScript Date object.
 *
 * @param {string} dateStr - The date string in "YYYYMMDD000000" format.
 * @returns {Date|null} A valid Date object or null if the input is invalid.
 */
export const convertYYYYMMDD000000ToDate = (dateStr) => {
    if (!dateStr || dateStr.length < 8) {
        console.error('Invalid date string:', dateStr)
        return null
    }
    const year = parseInt(dateStr.slice(0, 4), 10)
    const month = parseInt(dateStr.slice(4, 6), 10) - 1 // Months are 0-based in JavaScript
    const day = parseInt(dateStr.slice(6, 8), 10)
    return new Date(year, month, day)
}

/**
 * Returns a formatted date string in the format "YYYY/MM/DD HH:MM"
 * for use as an entry timestamp (e.g., logs, user activity, etc.).
 *
 * Example output: "2025/04/16 09:06"
 *
 * @param {Date} - Optional JavaScript Date object. Defaults to current date and time.
 * @returns {string} A formatted date string in "YYYY/MM/DD HH:MM" format.
 */
export const getFormattedEntryDateString = (date = new Date()) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}/${month}/${day} ${hours}:${minutes}`
}

export const setWithExpiry = (key, value, ttl) => {
    if (typeof window === 'undefined') return null
    const now = new Date()
    const item = {
        value,
        expiry: now.getTime() + ttl,
    }
    localStorage.setItem(key, JSON.stringify(item))
}

export const getWithExpiry = (key) => {
    if (typeof window === 'undefined') return null
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null

    const item = JSON.parse(itemStr)
    const now = new Date()

    if (now.getTime() > item.expiry) {
        localStorage.removeItem(key)
        return null
    }

    return true
}

/**
 * Generates a random GUID (UUID v4 format).
 *
 * @returns {string} A randomly generated GUID in the format "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".
 *
 * - Uses `Math.random()` to generate random values.
 * - Ensures the correct UUID v4 format by setting specific bits.
 * - Converts values to hexadecimal representation.
 */
export const generateGUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        let r = (Math.random() * 16) | 0 // Generates a random number between 0-15
        let v = c === 'x' ? r : (r & 0x3) | 0x8 // Ensures correct UUID v4 format
        return v.toString(16) // Converts to hex
    })
}

/**
 * Fetch a new access token from the API.
 * @param {string} apiUrl - The API endpoint to fetch the token.
 * @param {string} accessTokenKey - The key for storing the access token in local storage.
 * @param {string} tokenExpirationKey - The key for storing the token expiration time in local storage.
 * @returns {Promise<string>} - The new access token.
 */
export const fetchAccessToken = async (apiUrl, accessTokenKey, tokenExpirationKey, locale) => {
    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({locale}),
        })

        if (!response.ok) {
            throw new Error('Failed to fetch access token')
        }

        const data = await response.json()
        if (data.accessToken && data.expiresIn) {
            const expirationTime = Date.now() + data.expiresIn * 1000 // Convert expiresIn from seconds to milliseconds
            localStorage.setItem(accessTokenKey, data.accessToken)
            localStorage.setItem(tokenExpirationKey, expirationTime.toString())
            return data.accessToken
        } else {
            throw new Error('Invalid token response from API')
        }
    } catch (error) {
        console.error('Error fetching access token:', error)
        throw error
    }
}

/**
 * Get a valid token from local storage or fetch a new one if missing/expired.
 * @param {string} apiUrl - The API endpoint to fetch the token.
 * @param {string} accessTokenKey - The key for storing the access token in local storage.
 * @param {string} tokenExpirationKey - The key for storing the token expiration time in local storage.
 * @returns {Promise<string>} - A valid access token.
 */
export const getValidToken = async (apiUrl, accessTokenKey, tokenExpirationKey, locale) => {
    const storedToken = localStorage.getItem(accessTokenKey)
    const storedExpiration = localStorage.getItem(tokenExpirationKey)

    if (storedToken && storedExpiration) {
        const isTokenExpired = Date.now() >= Number(storedExpiration)
        if (!isTokenExpired) {
            return storedToken
        }
    }

    // Fetch a new token if no valid token exists
    return await fetchAccessToken(apiUrl, accessTokenKey, tokenExpirationKey, locale)
}

/**
 * Clears the stored access token.
 * @param {string} accessTokenKey - The key for the access token in local storage.
 * @param {string} tokenExpirationKey - The key for the expiration time in local storage.
 */
export const clearAuthToken = (accessTokenKey, tokenExpirationKey) => {
    localStorage.removeItem(accessTokenKey)
    localStorage.removeItem(tokenExpirationKey)
}

export const hashSHA256 = async (str) => {
    if (!str) return
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArr = Array.from(new Uint8Array(hashBuffer))
    return hashArr.map((x) => x.toString(16).padStart(2, '0')).join('')
}

/**
 * - This function is used to apply the hmacSHA256 encryption on a key - data pair
 * @param {String} key - The key used in the encription process
 * @param {String} data - The stringified JSON object
 * @returns {String} - A string that has applied the hmacSHA256 encription using the key and the data
 */
export const hmacSHA256 = async (key, data) => {
    if (!key || !data) return

    const encodedKey = new TextEncoder().encode(key)
    const encodedData = new TextEncoder().encode(data)

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        encodedKey,
        {name: 'HMAC', hash: {name: 'SHA-256'}},
        false,
        ['sign'],
    )

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, encodedData)
    return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
}

/**
 *
 * @param {*} code
 * @returns The language on its respective locale
 * @example
 * getLanguage('en') // English
 * getLanguage('it') // Italiano
 * getLanguage('fr') // Français
 */

export const getLanguage = (code) => {
    const lang = new Intl.DisplayNames([code], {type: 'language'})
    return lang.of(code)
}

/**
 *
 * @param {*} code
 * @returns The region on its respective locale
 * @example
 */

export const getRegion = (language, country) => {
    const lang = new Intl.DisplayNames([language], {type: 'region'})
    return lang.of(country)
}

// Function to calculate the initial map of material groups
export const calculateCountMap = (products) => {
    return Object.keys(products).reduce((acc, materialGroup) => {
        acc[materialGroup] = 0
        return acc
    }, {})
}

/**
 * Sums Account.Counter fields for each material group ONCE using only the first record's Account.
 * @param {Object} materialGroupCount - The map of material group codes to current counts (will be mutated).
 * @param {Array} orderSummaries - Array of order summary objects (from OMS API).
 */
export const sumAccountCountersForMaterialGroups = (materialGroupCount, orderSummaries) => {
    if (!orderSummaries || orderSummaries.length === 0) return
    const account = orderSummaries[0].Account || {}
    Object.keys(materialGroupCount).forEach((materialGroup) => {
        const counterField = `Counter${materialGroup}__c`
        const counterValue = account[counterField]
        if (
            Object.prototype.hasOwnProperty.call(account, counterField) &&
            !isNaN(parseInt(counterValue))
        ) {
            materialGroupCount[materialGroup] += parseInt(counterValue) || 0
        }
    })
}

// Function to update the map with the order records
export const updateCountMap = (countMap, records) => {
    // Sum OrderItemSummaries quantities
    records
        .flatMap((order) => order.OrderItemSummaries?.records || [])
        .forEach((item) => {
            if (countMap[item.ProductMaterialGroup__c] !== undefined) {
                countMap[item.ProductMaterialGroup__c] += item.Quantity || 0
            }
        })
}

// Function to get the material groups with the display value
export const getMaterialGroups = (products, countMap) => {
    return Object.entries(products).map(([materialGroup, maxValue]) => ({
        materialGroup,
        displayValue: `${countMap[materialGroup]} / ${maxValue}`,
    }))
}

/**
 * Generates meta tags based on the provided meta tag rules.
 * For each object in the array `metaTags`, the function creates a `<meta>` tag.
 * - If the `ID` of the meta tag is "description", it uses `name` as the attribute.
 * - For all other IDs, it uses `property` as the attribute.
 * @param {Array} metaTags - An array of objects where each object represents a meta tag rule.
 * @param {string} defaultTitle - The default title to use if no title meta tag is found.
 * @returns {JSX.Element[]} An array of JSX elements representing the meta tags, ready to be rendered.
 */
export const getMetaTags = (metaTags = [], defaultTitle = BRAND_LABEL) => {
    const hasTitleTag = metaTags?.some(
        (metaTag) => metaTag.ID === 'title' || metaTag.id === 'title',
    )

    const metaElements = metaTags?.map((metaTag, index) => {
        const isDescriptionTag = metaTag.ID === 'description' || metaTag.id === 'description'
        const isTitleTag = metaTag.ID === 'title' || metaTag.id === 'title'
        const key = `meta-tag-${index}`
        if (isTitleTag) {
            return <title key={key}>{metaTag.content || metaTag.value}</title>
        }
        return (
            <meta
                key={key}
                {...(isDescriptionTag
                    ? {name: metaTag.ID || metaTag.id}
                    : {property: metaTag.ID || metaTag.id})}
                content={metaTag.content || metaTag.value || ''}
            />
        )
    })
    if (!hasTitleTag) {
        metaElements.unshift(<title key="default-title">{defaultTitle}</title>)
    }

    return metaElements
}

/**
 * Parses a JSON string after removing all whitespace characters and correcting trailing commas.
 *
 * @param {string} string - The JSON string to parse.
 * @returns {Object|null} The parsed JSON object, or null if the input string is falsy.
 */
export const parseAndSanitizeJSON = (string) => {
    if (!string) return null

    try {
        string = string.replaceAll(/\s/g, '').replaceAll(',}', '}').replaceAll('}{', '},{')
        return JSON.parse(string)
    } catch (e) {
        console.error(string, e)
        return ''
    }
}

/**
 * Parses a JSON string after removing newlines, tabs, spaces around JSON symbols,fixes trailing commas,ensures objects are comma-separated
 * @param {string} string - THe JSON string to parse.
 * @returns {Object|null} The parsed JSON object, or null if the input string is falsy.
 */
export const parseAndSanitizeJSONWithSpaces = (string, id) => {
    if (!string) return null

    try {
        string = string
            ?.replace(/\n/g, '')
            .replace(/\t/g, '')
            .replace(/\s*([{},:[\]])\s*/g, '$1')
            .replace(/,}/g, '}')
            .replace(/}{/g, '},{')
        return JSON.parse(string)
    } catch (e) {
        console.error(string, id, e)
        return ''
    }
}

export const generateRandomString = (length) => {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return result
}

export const addDays = (days, date) => {
    const today = date
    return new Date(today?.getTime() + days * 24 * 60 * 60 * 1000)
}

export const splitArrayIntoChunks = (array, chunkSize = 10) => {
    const chunks = []
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize))
    }
    return chunks
}

/**
 * Calculates the index of an item based on the page number, item position, and offset.
 * Ensures a progressive index across chunks within the same page.
 * When `isPlpPlus` is true and `page === 1`, the first page contains 14 products.
 *
 * @param {number} page - The current page number.
 * @param {number} position - The position of the item within the chunk.
 * @param {number} offset - The cumulative size of all previous chunks.
 * @param {boolean} isPlpPlus - Whether the page is PLP Plus.
 * @returns {number} The calculated index.
 */
export const calculateIndex = (page, position, offset, isPlpPlus) => {
    const productsPerPage = isPlpPlus && page === 1 ? 14 : 16
    return position + 1 + (page - 1) * productsPerPage + offset
}

export const validatePostalCode = (countryCode, postalCode) => {
    const regexByCountry = {
        AF: /^$/, // Afghanistan - No postal code system
        AL: /^\d{4}$/, // Albania
        DZ: /^\d{5}$/, // Algeria
        AD: /^AD\d{3}$/, // Andorra
        AO: /^$/, // Angola - No postal code system
        AG: /^$/, // Antigua and Barbuda - No postal code system
        AR: /^[A-Z]?\d{4}[A-Z]{3}$/, // Argentina (CPA)
        AM: /^\d{4}$/, // Armenia
        AU: /^\d{4}$/, // Australia
        AT: /^\d{4}$/, // Austria
        AZ: /^AZ\d{4}$/, // Azerbaijan
        BS: /^$/, // Bahamas - No postal code system
        BH: /^\d{3,4}$/, // Bahrain
        BD: /^\d{4}$/, // Bangladesh
        BB: /^BB\d{5}$/, // Barbados
        BY: /^\d{6}$/, // Belarus
        BE: /^\d{4}$/, // Belgium
        BZ: /^$/, // Belize - No postal code system
        BJ: /^\d{2}$/, // Benin
        BM: /^[A-Z]{2}\d{2}$/, // Bermuda
        BT: /^$/, // Bhutan - No postal code system
        BO: /^\d{4}$/, // Bolivia
        BA: /^\d{5}$/, // Bosnia and Herzegovina
        BW: /^$/, // Botswana - No postal code system
        BR: /^\d{5}-?\d{3}$/, // Brazil
        BN: /^[A-Z]{2}\d{4}$/, // Brunei
        BG: /^\d{4}$/, // Bulgaria
        BF: /^$/, // Burkina Faso - No postal code system
        BI: /^\d{5}$/, // Burundi
        KH: /^\d{5}$/, // Cambodia
        CM: /^$/, // Cameroon - No postal code system
        CA: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/, // Canada
        CV: /^\d{4}$/, // Cape Verde
        CF: /^$/, // Central African Republic - No postal code system
        TD: /^$/, // Chad - No postal code system
        CL: /^\d{7}$/, // Chile
        CN: /^\d{6}$/, // China
        CO: /^\d{6}$/, // Colombia
        KM: /^$/, // Comoros - No postal code system
        CG: /^$/, // Congo - No postal code system
        CD: /^$/, // Congo, Democratic Republic - No postal code system
        CR: /^\d{4,5}$/, // Costa Rica
        CI: /^\d{2,5}$/, // Côte d'Ivoire
        HR: /^\d{5}$/, // Croatia
        CU: /^$/, // Cuba - No postal code system
        CY: /^\d{4}$/, // Cyprus
        CZ: /^\d{3}\s?\d{2}$/, // Czech Republic
        DK: /^\d{4}$/, // Denmark
        DJ: /^$/, // Djibouti - No postal code system
        DM: /^$/, // Dominica - No postal code system
        DO: /^\d{5}$/, // Dominican Republic
        EC: /^\d{6}$/, // Ecuador
        EG: /^\d{5}$/, // Egypt
        SV: /^\d{4}$/, // El Salvador
        GQ: /^$/, // Equatorial Guinea - No postal code system
        ER: /^$/, // Eritrea - No postal code system
        EE: /^\d{5}$/, // Estonia
        SZ: /^[A-Z]\d{3}$/, // Eswatini (Swaziland)
        ET: /^\d{4}$/, // Ethiopia
        FJ: /^$/, // Fiji - No postal code system
        FI: /^\d{5}$/, // Finland
        FR: /^\d{5}$/, // France
        GA: /^\d{2}\s?\d{3}$/, // Gabon
        GM: /^\d{5}$/, // Gambia
        GE: /^\d{4}$/, // Georgia
        DE: /^\d{5}$/, // Germany
        GH: /^\d{6}$/, // Ghana
        GR: /^\d{3}\s?\d{2}$/, // Greece
        GD: /^$/, // Grenada - No postal code system
        GT: /^\d{5}$/, // Guatemala
        GN: /^\d{3}$/, // Guinea
        GW: /^$/, // Guinea-Bissau - No postal code system
        GY: /^\d{5}$/, // Guyana
        HT: /^HT\d{4}$/, // Haiti
        HN: /^\d{5}$/, // Honduras
        HK: /^\d{6}$/, // Hong Kong
        HU: /^\d{4}$/, // Hungary
        IS: /^\d{3}$/, // Iceland
        IN: /^\d{6}$/, // India
        ID: /^\d{5}$/, // Indonesia
        IR: /^\d{10}$/, // Iran
        IQ: /^\d{5}$/, // Iraq
        IE: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/, // Ireland (Eircode)
        IL: /^\d{5,7}$/, // Israel
        IT: /^\d{5}$/, // Italy
        JM: /^[A-Z]{2}\d{2}$/, // Jamaica
        JP: /^\d{3}-\d{4}$/, // Japan
        JO: /^\d{5}$/, // Jordan
        KZ: /^\d{6}$/, // Kazakhstan
        KE: /^\d{5}$/, // Kenya
        KI: /^$/, // Kiribati - No postal code system
        KP: /^$/, // North Korea - No postal code system
        KR: /^\d{5}$/, // South Korea
        KW: /^\d{5}$/, // Kuwait
        KG: /^\d{6}$/, // Kyrgyzstan
        LA: /^\d{5}$/, // Laos
        LV: /^(LV-)?\d{4}$/, // Latvia
        LB: /^\d{4}(\s?\d{4})?$/, // Lebanon
        LS: /^\d{3}$/, // Lesotho
        LR: /^$/, // Liberia - No postal code system
        LY: /^$/, // Libya - No postal code system
        LI: /^\d{4}$/, // Liechtenstein
        LT: /^(LT-)?\d{5}$/, // Lithuania
        LU: /^\d{4}$/, // Luxembourg
        MO: /^\d{6}$/, // Macau
        MG: /^\d{3}$/, // Madagascar
        MW: /^$/, // Malawi - No postal code system
        MY: /^\d{5}$/, // Malaysia
        MV: /^$/, // Maldives - No postal code system
        ML: /^$/, // Mali - No postal code system
        MT: /^[A-Z]{3}\s?\d{4}$/, // Malta
        MH: /^$/, // Marshall Islands - No postal code system
        MR: /^$/, // Mauritania - No postal code system
        MU: /^\d{5}$/, // Mauritius
        MX: /^\d{5}$/, // Mexico
        FM: /^$/, // Micronesia - No postal code system
        MD: /^(MD-)?\d{4}$/, // Moldova
        MC: /^980\d{2}$/, // Monaco
        MN: /^\d{5}$/, // Mongolia
        ME: /^\d{5}$/, // Montenegro
        MA: /^\d{5}$/, // Morocco
        MZ: /^\d{4}$/, // Mozambique
        MM: /^$/, // Myanmar - No postal code system
        NA: /^\d{5}$/, // Namibia
        NR: /^$/, // Nauru - No postal code system
        NP: /^\d{5}$/, // Nepal
        NL: /^\d{4}\s?[A-Z]{2}$/, // Netherlands
        NZ: /^\d{4}$/, // New Zealand
        NI: /^\d{5}$/, // Nicaragua
        NE: /^\d{4}$/, // Niger
        NG: /^\d{6}$/, // Nigeria
        NO: /^\d{4}$/, // Norway
        OM: /^\d{3}$/, // Oman
        PK: /^\d{5}$/, // Pakistan
        PW: /^$/, // Palau - No postal code system
        PA: /^\d{4}$/, // Panama
        PG: /^$/, // Papua New Guinea - No postal code system
        PY: /^\d{4}$/, // Paraguay
        PE: /^\d{5}$/, // Peru
        PH: /^\d{4}$/, // Philippines
        PL: /^\d{2}-\d{3}$/, // Poland
        PT: /^\d{4}-\d{3}$/, // Portugal
        QA: /^\d{5}$/, // Qatar
        RO: /^\d{6}$/, // Romania
        RU: /^\d{6}$/, // Russia
        RW: /^\d{4}$/, // Rwanda
        KN: /^$/, // Saint Kitts and Nevis - No postal code system
        LC: /^[A-Z]{2}\d{2}\d{3}$/, // Saint Lucia
        VC: /^$/, // Saint Vincent and the Grenadines - No postal code system
        WS: /^$/, // Samoa - No postal code system
        SM: /^4789\d$/, // San Marino
        ST: /^$/, // Sao Tome and Principe - No postal code system
        SA: /^\d{5}(-\d{4})?$/, // Saudi Arabia
        SN: /^\d{5}$/, // Senegal
        RS: /^\d{5}$/, // Serbia
        SC: /^$/, // Seychelles - No postal code system
        SL: /^$/, // Sierra Leone - No postal code system
        SG: /^\d{6}$/, // Singapore
        SK: /^\d{3}\s?\d{2}$/, // Slovakia
        SI: /^\d{4}$/, // Slovenia
        SB: /^$/, // Solomon Islands - No postal code system
        SO: /^$/, // Somalia - No postal code system
        ZA: /^\d{4}$/, // South Africa
        SS: /^$/, // South Sudan - No postal code system
        ES: /^\d{5}$/, // Spain
        LK: /^\d{5}$/, // Sri Lanka
        SD: /^$/, // Sudan - No postal code system
        SR: /^$/, // Suriname - No postal code system
        SE: /^\d{3}\s\d{2}$/, // Sweden
        CH: /^\d{4}$/, // Switzerland
        SY: /^$/, // Syria - No postal code system
        TW: /^\d{3}(?:\d{2})?$/, // Taiwan
        TJ: /^\d{6}$/, // Tajikistan
        TZ: /^\d{5}$/, // Tanzania
        TH: /^\d{5}$/, // Thailand
        TL: /^$/, // Timor-Leste - No postal code system
        TG: /^$/, // Togo - No postal code system
        TO: /^$/, // Tonga - No postal code system
        TT: /^\d{6}$/, // Trinidad and Tobago
        TN: /^\d{4}$/, // Tunisia
        TR: /^\d{5}$/, // Turkey
        TM: /^\d{6}$/, // Turkmenistan
        TV: /^$/, // Tuvalu - No postal code system
        UG: /^$/, // Uganda - No postal code system
        UA: /^\d{5}$/, // Ukraine
        AE: /^$/, // United Arab Emirates - No postal code system
        GB: /^([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})$/, // United Kingdom
        US: /^\d{5}(-\d{4})?$/, // United States
        UY: /^\d{5}$/, // Uruguay
        UZ: /^\d{6}$/, // Uzbekistan
        VU: /^$/, // Vanuatu - No postal code system
        VA: /^00120$/, // Vatican
        VE: /^\d{4}$/, // Venezuela
        VN: /^\d{6}$/, // Vietnam
        YE: /^$/, // Yemen - No postal code system
        ZM: /^$/, // Zambia - No postal code system
        ZW: /^$/, // Zimbabwe - No postal code system
    }

    // Retrieve the regex pattern for the provided country code
    const pattern = regexByCountry[countryCode.toUpperCase()]

    if (!pattern) {
        // If checking for an unlisted country, return false
        return false
    }

    // Test if the postal code matches the pattern
    return pattern.test(postalCode.trim())
}

/**
 * Retrieves the appropriate image for a product tile.
 *
 * @param {Object} product - The product object containing image and variant details.
 * @param {string|null} customImage - Optional custom image URL to override the default image.
 * @returns {string|null} The URL of the image to display for the product tile, or null if no image is found.
 */
export const getImageForProductTile = (product, customImage = null) => {
    let image = customImage ? customImage : product?.c_image?.link

    if (!image) {
        const productVariant = product?.variants?.find(
            (variant) => variant?.productId === product?.representedProduct?.id,
        )

        const variantImage = findImageGroupBy(product?.imageGroups, {
            viewType: 'small',
            selectedVariationAttributes:
                productVariant?.variationValues ?? product?.variationValues,
        })

        image = variantImage?.images?.[0]?.link
    }

    return image || null
}

export const decodeHtmlEntities = (text) => {
    if (!text) return ''
    const entities = {
        '&rsquo;': '’',
        '&lsquo;': '‘',
        '&quot;': '"',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
    }
    return text.replace(/&[a-z]+;/g, (match) => entities[match] || match)
}

export const buildHmcString = (BRAND, retrievedProducts, answers) => {
    if (!answers) return
    const skus =
        retrievedProducts
            ?.map((p) => p?.variants?.[0]?.productId)
            .filter(Boolean)
            .slice(0, 3) || []

    const answerEntries = Object.entries(answers).map(([, value], index) => [
        `answer${index + 1}`,
        value,
    ])

    return {
        hmc: {
            [BRAND]: {
                date: new Date().toISOString().slice(0, 10),
                skus,
                ...Object.fromEntries(answerEntries),
            },
        },
    }
}

export const buildCampaignTags = (
    psp,
    BRAND,
    BRAND_SHORT_LABEL,
    campaignTag,
    retrievedProducts,
    locale,
) => {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '')
    const brandLocale = `${BRAND}${locale.toUpperCase()}`
    const skus =
        retrievedProducts
            ?.map((p) => p?.variants?.[0]?.productId)
            .filter(Boolean)
            .slice(0, 3) || []

    if (psp) {
        return `${today} ${campaignTag} ${brandLocale}`
    } else {
        const skuString = skus.join('|')
        return `${today} HMC${BRAND_SHORT_LABEL.toUpperCase()}-${skuString} ${brandLocale}`
    }
}

export const cssObjectToString = (styles) => {
    return Object.entries(styles)
        .map(([selector, rules]) => {
            if (selector.startsWith('@media')) {
                const mediaRules = Object.entries(rules)
                    .map(([innerSelector, innerStyles]) => {
                        const innerRuleBody = Object.entries(innerStyles)
                            .map(([prop, val]) => {
                                const propKebab = prop.replace(/([A-Z])/g, '-$1').toLowerCase()
                                return `${propKebab}: ${val};`
                            })
                            .join(' ')
                        return `${innerSelector} { ${innerRuleBody} }`
                    })
                    .join(' ')
                return `${selector} { ${mediaRules} }`
            } else {
                const ruleBody = Object.entries(rules)
                    .map(([property, value]) => {
                        const propKebab = property.replace(/([A-Z])/g, '-$1').toLowerCase()
                        return `${propKebab}: ${value};`
                    })
                    .join(' ')
                return `${selector} { ${ruleBody} }`
            }
        })
        .join('\n')
}

export * from '@salesforce/retail-react-app/app/utils/utils.js'
